import React, { useState } from "react"
import tw, { styled, css } from "twin.macro"
import { Description, Field, HelpText, InputType, Label } from "./input"
import { ReactComponent as IconEyeSVG } from "../../assets/input/eye-open.svg"
import { ReactComponent as IconEyeClosedSVG } from "../../assets/input/eye-closed.svg"

export interface PasswordInputProps {
  inputValue: string
  name: string
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleFocus?: () => Promise<void> | void
  disabled?: boolean
  autoComplete: string
  required?: boolean
  label?: string
  description?: string
  placeholder?: string
  helpText?: string
  tabIndex?: number
  invalid?: boolean
}
interface StyledProps {
  invalid?: boolean | undefined
  onClick?: () => void
}

const StyledIcon = styled("span")(({ onClick }: StyledProps) => [
  tw`px-4 absolute top-2.5 right-0`,
  onClick && tw`cursor-pointer`,
])
const StyledInput = styled.input(({ invalid }: StyledProps) => [
  tw`w-full p-2`,
  tw`text-base-6 bg-input caret-purple`,
  tw`(overflow-hidden whitespace-nowrap overflow-ellipsis)`,
  tw`(border border-base-3 rounded)`,
  tw`placeholder:(text-base-3)`,
  tw`focus:(outline-none border-purple)`,
  tw`disabled:(text-base-2 border-base-2 placeholder:(text-base-2))`,
  tw`read-only:(focus:border-base-3)`,
  invalid && tw`(border-red)`,
  css`
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `,
])

export const PasswordInput = ({
  inputValue,
  name,
  handleChange,
  handleFocus,
  disabled,
  autoComplete,
  required,
  tabIndex,
  label,
  description,
  placeholder,
  helpText,
  invalid,
}: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const togglePassword = () => setShowPassword(!showPassword)

  return (
    <Field>
      <Label data-testid={`${name}-label`} htmlFor={`${name}-id`}>
        {label}
      </Label>
      {description && (
        <Description id={`${name}-description`} data-testid={`${name}-description`}>
          {description}
        </Description>
      )}
      <div tw="relative">
        <StyledIcon onClick={togglePassword}>
          {showPassword ? <IconEyeSVG data-testid="eye-icon" /> : <IconEyeClosedSVG data-testid="eye-close-icon" />}
        </StyledIcon>
        <StyledInput
          data-testid={`${name}-input`}
          autoComplete={autoComplete} // value has dashes will not work in chrome
          tabIndex={tabIndex}
          type={showPassword ? InputType.Text : InputType.Password}
          name={name}
          id={`${name}-id`}
          onChange={handleChange}
          onFocus={handleFocus}
          value={inputValue}
          placeholder={placeholder}
          aria-label={label}
          aria-describedby={`${name}-description ${name}-helptext`}
          disabled={disabled}
          required={required}
          aria-required={required}
          invalid={invalid}
          data-private
        />
      </div>
      <HelpText id={`${name}-helptext`} data-testid={`${name}-help-text`} invalid={invalid}>
        {helpText}
      </HelpText>
    </Field>
  )
}
